// JackedLogs design language v1 — canonical token source.
//
// Single source of truth for every surface: apps/mobile and apps/web-rnw
// consume these values directly through @jackedlogs/ui, and the frozen
// legacy apps/web mirrors them by hand in index.css.
//
// The stack is React Native + React Native Web. There is no Tamagui and no
// tamagui.config.ts — an older version of this header claimed both, which
// sent readers looking for a build step that never existed (DS-24).
//
// Spec: DESIGN.md at the repo root. (DESIGN_LANGUAGE.md is archived under
// docs/archive/ and stale — it still names the pre-2026-06-29 font.)

export const palette = {
  light: {
    brand500: "#ff5a1f",
    brand600: "#e84a10",
    brand100: "#fff1eb",

    // DS-11 + DS-21 (Antenore, 2026-07-26) — `brand600` was doing two jobs at
    // once: a TEXT colour on surfaces, and the PRESSED fill under every brand
    // button. One hex can't serve both, because the two want opposite things —
    // text wants contrast against a pale surface, a fill wants to read as the
    // same button, only pushed. That collision is what parked brand600 outside
    // the on-fill gate below for two weeks.
    //
    // The three roles that replace it:
    //   brandText          — the brand hue AS TEXT. Gated >=4.5:1 on
    //                        bg/surface/surface2 AND on brand100 (the
    //                        instructional pill's own tint, which is the
    //                        surface DS-11 was called for).
    //   brandPressed       — the pressed fill. Every family starts at exactly
    //                        the brand600 it already shipped, so nothing moves
    //                        visually on this commit.
    //   inkOnBrandPressed  — the ink that rides that fill. DS-21 variant A:
    //                        pressing darkens the button and THE INK FOLLOWS
    //                        THE FILL, which is IB-11's lesson applied to the
    //                        brand. Equal to today's `inkOnBrand` in five
    //                        families of six; rosepine-light is the exception
    //                        (see its note in themes.ts).
    //
    // brand600 stays put until VB-PAL-2/VB-PAL-3 empty its call sites.
    brandText: "#c53f0e",
    brandPressed: "#e84a10",
    inkOnBrandPressed: "#0a0c0d",

    bg: "#faf8f5",
    surface: "#ffffff",
    surface2: "#f3f0eb",
    border: "#e5e2dc",
    // DS-9 first: a borderStrong that can't mark a control boundary isn't
    // strong (was #cfcbc2 → 1.62:1 vs surface).
    //
    // DS-23 (Antenore, 2026-07-26) finished the job. DS-9 tuned this against
    // `surface` and never against `surface2` — which is where Badge neutral,
    // the superset cue and disabled buttons actually draw it. Measured there,
    // five families of six were UNDER 3:1 (2.66 · 2.82 · 2.99 · 2.63 · 2.79;
    // only catppuccin-dark passed, at 3.12). That matters more than it looks:
    // `surface` and `surface2` are separated by just 1.08–1.15 across all six,
    // so this stack has always been held up by the border rather than by the
    // fill difference. A border under 3:1 there means two stacked surfaces
    // with no legible boundary at all. Retuned to ≥3.20 everywhere.
    borderStrong: "#8a857b",
    // Modal/sheet backdrop dim. Flat black-alpha in BOTH themes (a scrim
    // darkens content regardless of theme) — replaces the 4 disagreeing
    // hand-picked literals (#0008 / rgba(0,0,0,0.4|0.45|0.5)) the ~13
    // hand-rolled modals each re-rolled. Consumed by the <Modal> primitive.
    scrim: "rgba(0,0,0,0.5)",

    ink: "#0a0c0d",
    ink2: "#3c4043",
    // DS-8 comfort: was #686c70 at 4.66 — floor-grade for no reason, since this
    // family has 17.24 of headroom (unlike catppuccin/rosepine light, where the
    // palette's own text tone caps it near 6). ink2 above it reads 9.21, so the
    // gap was all in the wrong place → 6.41.
    ink3: "#53575b",
    // DS-20 (2026-07-14): was #ffffff — 3.12:1 on brand500 and 3.88 on brand600,
    // i.e. every primary button in the DEFAULT theme failed AA text. The signature
    // orange is NOT the problem and does not move: white simply cannot sit on it.
    // Dark ink takes brand500 to 6.29 and brand600 to 5.06, and it is what the
    // dark theme already does with the same orange. Found by the on-fill gate
    // below; Antenore chose the ink over touching the brand.
    inkOnBrand: "#0a0c0d",
    // IB-11 (2026-07-14): one "ink on a fill" token can't serve fills that sit
    // on OPPOSITE sides of mid-grey. `inkOnBrand` follows brand500's lightness;
    // where a family's danger/warn land the other way, reusing it puts dark on
    // dark. That is how rosepine-light shipped inkOnBrand #312e44 on danger
    // #955265 = 2.29:1 — the PR trophy and the fail pill both near-invisible.
    // Each semantic fill now names its own ink; paletteContrast.test.ts gates
    // every (ink, fill) pair on every family and mode. Same idea the effortRamp
    // already used with its {fill, on} pairs.
    inkOnDanger: "#ffffff",
    inkOnWarn: "#ffffff",

    // Text-grade dark gold in light (DS-10, Antenore 2026-07-04): the bright
    // gold was 1.53:1 on prFill — trophy, borders and PR text vanished.
    // Catppuccin and rosepine already do exactly this (their light pr equals
    // their dark-gold warn); the bright celebration gold lives on in dark.
    pr: "#7a5900",
    prFill: "#fff8e0",
    // Text-grade dark green in light (DS-13, 2026-07-07): the bright #1aa872 was
    // 3.05:1 on surface / 2.68 on surface2 — the "on target" state text + zone
    // fill vanished for reduced vision. Darkened to ≥4.5:1 on surface/surface2/bg
    // (5.85 / 5.15 / 5.52), matching how catppuccin/rosepine already darken their
    // light `complete`. The bright green lives on in dark. Fill token unchanged.
    complete: "#0e7350",
    completeFill: "#e3f7ee",
    danger: "#d93025",
    dangerFill: "#fce8e6",
    warn: "#7a5900",
    warnFill: "#fbf0db",
  },
  dark: {
    brand500: "#ff5a1f",
    brand600: "#ff7842",
    brand100: "#3a1a0d",

    // DS-11 + DS-21, see the light twin. Dark never had the failure that forced
    // the split — its brand600 already reads as text — so brandText equals it.
    brandText: "#ff7842",
    brandPressed: "#ff7842",
    inkOnBrandPressed: "#0a0c0d",

    bg: "#0e1011",
    surface: "#161819",
    surface2: "#1d1f21",
    border: "#2a2c2e",
    // DS-9 (was #3d4042 → 1.71:1 vs surface) + DS-23 retune vs surface2 — see
    // the light twin for why surface2 is the surface that decides this token.
    borderStrong: "#6a6e70",
    // Modal/sheet backdrop dim — same flat black-alpha as light (see note there).
    scrim: "rgba(0,0,0,0.5)",

    ink: "#f4f3ef",
    ink2: "#b9b6ae",
    // DS-8 comfort: 4.71 cleared the floor and nothing else, while ink2 above
    // it already read 8.16 — the tertiary tier was the lone straggler → 6.32.
    ink3: "#a3a096",
    inkOnBrand: "#0a0c0d",
    inkOnDanger: "#0a0c0d",
    inkOnWarn: "#0a0c0d",

    pr: "#ffd24a",
    prFill: "#2a1f08",
    complete: "#2bd47e",
    completeFill: "#0e2a1d",
    danger: "#ff5a52",
    dangerFill: "#2c1212",
    warn: "#f5c542",
    warnFill: "#2a2008",
  },
} as const;

// Intensity scale for the program-preview grid (day × week heat).
// A 4-band semantic scale distinct from the brand orange: red=heavy /
// green=work carries training meaning the coach reads at a glance, and
// `max` (the ≥85% peak band, user-facing "Max") is a cool indigo
// outlier ("a different kind of day"). Each band:
//   fill — the cell background (the glance colour)
//   on   — the peak-% number drawn ON the cell (variant B). White fails
//          AA on the amber `moderate`, so that one is dark on purpose.
//   soft — badge background tint (detail-panel day badge)
//   text — badge label colour
// `none` = a day/week with no percent-of-max compound (accessory/rest);
// rendered neutral, never mis-coloured. Bands derived in
// packages/shared/src/programIntensity.ts (>= thresholds).
export const intensityScale = {
  light: {
    work: { fill: "#2e9e7b", on: "#ffffff", soft: "#e3f3ee", text: "#0f5c46" },
    moderate: { fill: "#e0992a", on: "#3a2a05", soft: "#fbf0db", text: "#855214" },
    heavy: { fill: "#d8453c", on: "#ffffff", soft: "#fbe4e2", text: "#7a211c" },
    max: { fill: "#5b53c6", on: "#ffffff", soft: "#e7e5f7", text: "#322c7a" },
    none: { fill: "#e5e2dc", on: "#686c70", soft: "#f3f0eb", text: "#686c70" },
  },
  dark: {
    work: { fill: "#3fb389", on: "#06150f", soft: "#0e2a1f", text: "#7fe3c2" },
    moderate: { fill: "#e0a23e", on: "#241a05", soft: "#2a2008", text: "#f0c071" },
    heavy: { fill: "#e0564c", on: "#1a0606", soft: "#2c1212", text: "#f29a92" },
    max: { fill: "#7c74d8", on: "#0a0814", soft: "#1c1933", text: "#b0aae8" },
    none: { fill: "#2a2c2e", on: "#8c897c", soft: "#1d1f21", text: "#8c897c" },
  },
} as const;

// Worked-muscle volume heatmap ramp — 5 steps, low→high volume, painted on the
// body figure (Progress + post-session summary). SEMANTIC traffic-light: green
// (rested / low volume) → gold → orange → red (over-worked). Replaces the old
// grey-with-one-orange-peak ramp (Antenore 2026-07-01: "scala di grigi e un
// colore è no-go" — needs to read as rested→over-used, with the low/mid steps
// clearly distinct). Untrained muscles carry ZERO volume → they're not in the
// heatmap map at all → they render the body figure's neutral base (the "grey"
// Antenore wants kept for "non allenato"). Theme-aware; each family shifts the
// stops onto its own palette (themes.ts). The share card + the in-app ramp now
// both follow the theme. This is the `brand` family's ramp.
export const heatmapRamp = {
  light: ["#1aa872", "#7cbf3f", "#f2c31c", "#ef7f2d", "#d8453c"],
  dark: ["#2bd47e", "#93d63f", "#ffd23f", "#ff9838", "#e0564c"],
} as const;

// Effort-meter ramp — 5 steps, easy→hard (RIR 4+ → RIR 0), painted on the
// per-set effort meter (AP-0, Decision 8). MONOCHROME on the family accent
// hue — never a green→red traffic light: the colour says intensity, not
// merit ("easy" is a legitimate, sometimes deliberate choice — e.g. 20-30
// rep sets — and must not read as a mistake). Fail lives OUTSIDE this ramp
// (danger token): failing to move the bar is a different event, not a sixth
// step. Each stop carries its own `on` colour for the step number because a
// light→dark monochrome band crosses the mid-lightness zone where no single
// ink passes AA — the pair is validated ≥4.5:1 per stop, per mode, per
// family by effortRampContrast.test.ts (numeric gate, not eyeballed).
// Light mode reads pale tint (easy) → deep saturated (hard); dark mode reads
// dim (easy) → bright accent (hard). This is the `brand` family's ramp.
export const effortRamp = {
  light: [
    { fill: "#fcddcf", on: "#331a0f" },
    { fill: "#f9b79a", on: "#331a0f" },
    { fill: "#f58d61", on: "#331a0f" },
    { fill: "#c0420c", on: "#ffffff" },
    { fill: "#903209", on: "#ffffff" },
  ],
  dark: [
    { fill: "#65260b", on: "#f3ebe8" },
    { fill: "#8e3610", on: "#f3ebe8" },
    { fill: "#b84514", on: "#f3ebe8" },
    { fill: "#ed865a", on: "#26140d" },
    { fill: "#f2ab8c", on: "#26140d" },
  ],
} as const;

export const spacing = {
  s0: 0,
  s1: 2,
  s2: 4,
  s3: 8,
  s4: 12,
  s5: 16,
  s6: 24,
  s7: 32,
  s8: 48,
} as const;

export const radius = {
  r1: 2,
  r2: 4,
  r3: 6,
  r4: 10,
} as const;

export const typography = {
  sizes: {
    "2xs": 10,
    xs: 11,
    sm: 12,
    base: 14,
    md: 16,
    lg: 20,
    xl: 24,
    "2xl": 32,
  },
  leading: {
    tight: 1.15,
    snug: 1.3,
    base: 1.4,
  },
  // Web CSS stacks (the leading family is the bundled brand face; the rest are
  // system fallbacks shown only until the self-hosted @font-face loads). On
  // React Native these stacks are NOT valid `fontFamily` values — RN takes a
  // single registered family name — so @jackedlogs/ui resolves a platform
  // override (the bare "Archivo" / "JetBrains Mono" name registered natively).
  // See packages/ui/src/theme.ts `platformTypography`.
  fonts: {
    sans: '"Archivo", -apple-system, system-ui, "Segoe UI", Roboto, sans-serif',
    mono: '"JetBrains Mono", "Iosevka", ui-monospace, "SF Mono", "Liberation Mono", Consolas, monospace',
  },
} as const;

// RN-shaped shadow descriptors. The web's CSS uses `box-shadow:` but RN
// has its own (iOS shadow* + Android elevation), so we model them as
// objects keyed by theme. Mobile spreads them straight into a style
// object: `style={[..., shadows[scheme].soft]}`. Web mirrors the
// values manually in index.css until codegen lands (T2.5).
//
// Composite border tokens (`--rule`, `--rule-strong` on web) are
// deliberately NOT in this module — they're trivially derived from
// `palette.border{Strong}` + `borderWidth: 1` and modelling them
// twice would split the source of truth.
export const shadows = {
  light: {
    soft: {
      shadowColor: '#0a0c0d',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.04,
      shadowRadius: 0,
      elevation: 1,
    },
    modal: {
      shadowColor: '#0a0c0d',
      shadowOffset: { width: 0, height: 18 },
      shadowOpacity: 0.18,
      shadowRadius: 48,
      elevation: 12,
    },
  },
  dark: {
    soft: {
      shadowColor: '#000000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.3,
      shadowRadius: 0,
      elevation: 2,
    },
    modal: {
      shadowColor: '#000000',
      shadowOffset: { width: 0, height: 24 },
      shadowOpacity: 0.6,
      shadowRadius: 64,
      elevation: 18,
    },
  },
} as const;

export const motion = {
  fast: 80,
  medium: 160,
  slow: 320,
  easing: "cubic-bezier(0.2, 0, 0.2, 1)",
} as const;

export const layout = {
  railWidth: 232,
  railNarrow: 76,
  contentMax: 920,
  tabBarH: 56,
} as const;

// Tab-item visual language — single source of truth for the bottom tab
// bar (the "footer") AND the top experience switcher (the "header") on
// mobile, plus the future web-rnw nav. The footer is the reference; the
// header matches it so the two read identical (Antenore's model: the
// header tells you WHICH experience, the footer WHERE within it).
//
// Pure data → ports verbatim to react-native-web (the RNW strangler nav
// can consume these same tokens; nothing here imports react-native or
// react-navigation). `labelSize` mirrors the stock @react-navigation
// bottom-tab label (10) and the material icon box (24); the active/
// inactive weights drive a non-colour state cue (a11y: never colour
// alone) that the switcher applies and the footer gets natively.
export const tabBar = {
  iconSize: 24,
  labelSize: typography.sizes["2xs"], // 10 — the footer baseline
  labelWeight: "400",
  labelWeightActive: "600",
  itemMinHeight: 48, // ≥44pt tap target
} as const;

export type ThemeName = keyof typeof palette;
export type Palette = (typeof palette)[ThemeName];
export type IntensityScale = (typeof intensityScale)[ThemeName];
export type IntensityBandColors = IntensityScale[keyof IntensityScale];
export type SpacingKey = keyof typeof spacing;
export type RadiusKey = keyof typeof radius;
export type FontSizeKey = keyof typeof typography.sizes;
export type ShadowKey = keyof (typeof shadows)['light'];
